import React, { useEffect, useState } from 'react';
import Select, { components } from 'react-select';
import { MdKeyboardArrowRight } from 'react-icons/md';

import './Dropdown.scss';
import { UseMutation } from '@services/index';

export type DropdownOption = {
    id: string | number;
    label: string;
};

export type DropdownProps = {
    options?: DropdownOption[];
    onOptionChange: (option: DropdownOption) => void;
    value?: DropdownOption | string | number | null | undefined;
    menuIsOpen?: boolean;
    required?: boolean;
    isClearable?: boolean;
    label?: string;
    disabled?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationHook?: UseMutation<any[]>;
    isSearchable?: boolean;
    name?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DropdownIndicator = (props: any) => {
    return (
        <components.DropdownIndicator {...props}>
            <MdKeyboardArrowRight fontSize={28} />
        </components.DropdownIndicator>
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ValueContainer = ({ children, ...props }: any) => {
    return (
        <components.ValueContainer {...props}>
            <div className="dropdown__label">{props.selectProps.placeholder}</div>
            {children}
        </components.ValueContainer>
    );
};

export const Dropdown: React.FC<DropdownProps> = ({
    options = [],
    onOptionChange,
    label,
    value,
    mutationHook,
    menuIsOpen,
    disabled = false,
    isSearchable = false,
    name,
    required = false,
    isClearable = false
}) => {
    const [opt, setOpt] = useState<DropdownOption[]>(options);
    const [currentValue, setCurrentValue] = useState<DropdownOption | null | undefined>();
    const [getData, { isSuccess, data: allData }] = mutationHook ? mutationHook() : [null, {}];

    useEffect(() => {
        if (getData) getData();
    }, [getData]);

    useEffect(() => {
        if (!mutationHook) {
            setOpt(options);
        }
    }, [options, mutationHook]);

    useEffect(() => {
        const currentOptions = mutationHook ? opt : options;
        const option = currentOptions.find(
            (option) => option.id === value || option.id === (value as unknown as { id: string | number })?.id
        );
        setCurrentValue(option);
    }, [value, options, opt, mutationHook]);

    useEffect(() => {
        if (isSuccess && allData?.data) {
            setOpt(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                allData.data.map((d: any) => {
                    return { id: d.code ?? d.id, label: d.label };
                })
            );
        }
    }, [isSuccess, allData]);

    const finalOptions = mutationHook ? opt : options;

    return (
        <>
            <Select
                name={name}
                required={required}
                options={finalOptions}
                className="dropdown"
                classNamePrefix="dropdown"
                components={{
                    DropdownIndicator,
                    ValueContainer
                }}
                placeholder={label + (required ? ' *' : '')}
                onChange={(option) => onOptionChange(option as DropdownOption)}
                menuIsOpen={menuIsOpen}
                value={currentValue}
                getOptionValue={(option) => option.id.toString()}
                getOptionLabel={(option) => option.label}
                isSearchable={isSearchable}
                isDisabled={disabled}
                isClearable={isClearable}
            />
        </>
    );
};
